import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { client } from "../../..";
import {
  WA_ADMIN_OVERVIEW, WA_REFRESH_HEALTH, WA_SAVE_TEMPLATE_CONFIG, WA_AUDIT_LOG, WA_SEND_RULES, WA_SAVE_SEND_RULES,
  WA_TEMPLATES_ADMIN, WA_DELETE_TEMPLATE,
} from "../../../Queries/WhatsApp";
import { fetchWaStatus, fetchWaTemplates, fetchWaQuickReplies } from "../../../Slices/whatsappSlice";
import socket from "../Sales1/socket";
import QuickRepliesPanel from "./QuickRepliesPanel";
import TemplateEditorPanel from "./TemplateEditorPanel";
import { VARIABLE_SOURCE_LABELS, fullTime, gqlErrorMessage } from "./waUtils";
import "./whatsapp.css";

const RANGES = [7, 30, 90];
const TABS = [
  { key: "overview", label: "Overview" },
  { key: "templates", label: "Templates" },
  { key: "quick", label: "Quick replies" },
  { key: "audit", label: "Audit log" },
];
const QUALITY_HELP = {
  GREEN: "High quality — keep it up.",
  YELLOW: "Medium quality — customers are blocking or reporting some messages. Review recent templates.",
  RED: "Low quality — messaging limits may drop. Pause marketing templates and review opt-outs.",
  UNKNOWN: "Not rated yet (needs more conversations).",
};

const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : "—");

const Card = ({ label, value, sub }) => (
  <div className="wa-card">
    <div className="wa-card__label">{label}</div>
    <div className="wa-card__value">{value}</div>
    {sub && <div className="wa-card__sub">{sub}</div>}
  </div>
);

const Overview = ({ data, days, setDays, onRefreshHealth, refreshing }) => {
  const { health, stats, perUser, templateUsage, webhook, config, avgFirstResponseMinutes } = data;
  const sent = stats.outbound || 0;
  const byStatus = stats.outboundByStatus || {};
  const delivered = (byStatus.delivered || 0) + (byStatus.read || 0);
  const quality = health?.qualityRating || "UNKNOWN";
  const webhookStale = webhook.lastEventAt && Date.now() - new Date(webhook.lastEventAt).getTime() > 3 * 24 * 3600 * 1000;

  return (
    <>
      {!config.enabled && (
        <div className="wa-alert wa-alert--danger" style={{ marginBottom: 16 }}>
          <i className="fa-solid fa-plug-circle-xmark" /> WhatsApp is disabled — missing server env: <code>{(config.missing || []).join(", ")}</code>
        </div>
      )}
      <div className="wa-block">
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <h4 style={{ marginBottom: 4 }}>Number health</h4>
            <div className={`wa-quality wa-quality--${quality}`} style={{ fontSize: 20 }}>
              <i className="fa-solid fa-circle" style={{ fontSize: 12 }} /> {quality}
            </div>
            <div className="wa-help">{QUALITY_HELP[quality] || ""}</div>
          </div>
          <dl className="wa-record__grid" style={{ fontSize: 13 }}>
            <dt>Number</dt><dd>{health?.displayPhoneNumber || config.displayNumber}</dd>
            <dt>Display name</dt><dd>{health?.verifiedName || "—"} {health?.nameStatus ? `(${health.nameStatus.toLowerCase()})` : ""}</dd>
            <dt>Messaging tier</dt><dd>{health?.messagingLimitTier || "—"}</dd>
            <dt>Status</dt><dd>{health?.status || "—"}</dd>
            <dt>Last checked</dt><dd>{health?.checkedAt ? fullTime(health.checkedAt) : "Never"}</dd>
          </dl>
          <button type="button" className="wa-btn" onClick={onRefreshHealth} disabled={refreshing || !config.enabled}>
            <i className={`fa-solid fa-rotate ${refreshing ? "wa-spin" : ""}`} /> Check now
          </button>
        </div>
        {health?.history?.length > 1 && (
          <div className="wa-help" style={{ marginTop: 10 }}>
            History: {health.history.slice(0, 10).map((h) => `${h.qualityRating} (${new Date(h.at).toLocaleDateString()})`).join(" · ")}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 12, alignItems: "center" }}>
        <span className="wa-help" style={{ margin: 0 }}>Period:</span>
        {RANGES.map((r) => (
          <button type="button" key={r} className={`wa-btn wa-btn--sm ${days === r ? "wa-btn--primary" : ""}`} onClick={() => setDays(r)}>Last {r} days</button>
        ))}
      </div>

      <div className="wa-cards">
        <Card label="Messages received" value={stats.inbound} />
        <Card label="Messages sent" value={sent} sub={`${pct(delivered, sent)} delivered · ${pct(byStatus.read || 0, sent)} read`} />
        <Card label="Failed sends" value={byStatus.failed || 0} sub={pct(byStatus.failed || 0, sent)} />
        <Card label="New conversations" value={stats.newConversations} />
        <Card label="Avg first response" value={avgFirstResponseMinutes == null ? "—" : avgFirstResponseMinutes < 60 ? `${avgFirstResponseMinutes}m` : `${(avgFirstResponseMinutes / 60).toFixed(1)}h`} />
        <Card label="Opt-outs" value={stats.optOuts} />
        <Card label="Blocked sends" value={stats.blockedSends} sub="Guardrails (window, consent, limits)" />
      </div>

      <div className="wa-block">
        <h4>Webhook</h4>
        <dl className="wa-record__grid" style={{ fontSize: 13 }}>
          <dt>Callback path</dt><dd><code>{config.webhookPath}</code> (subscribe the <b>messages</b> field)</dd>
          <dt>Last event</dt><dd>{webhook.lastEventAt ? fullTime(webhook.lastEventAt) : "No events received yet"}</dd>
          <dt>Pending retries</dt><dd>{webhook.pendingEvents}</dd>
          <dt>Graph API</dt><dd>{config.graphApiVersion} · phone {config.phoneNumberId} · WABA {config.wabaId}</dd>
        </dl>
        {webhookStale && <div className="wa-alert wa-alert--warn" style={{ marginTop: 10 }}><i className="fa-solid fa-triangle-exclamation" /> No webhook events for over 3 days — check the webhook subscription in Meta.</div>}
      </div>

      <div className="wa-block">
        <h4>Team activity</h4>
        {!perUser.length ? <div className="wa-help">No messages sent in this period.</div> : (
          <table className="wa-table">
            <thead><tr><th>Team member</th><th>Sent</th><th>Templates</th><th>Read</th><th>Failed</th><th>Conversations</th></tr></thead>
            <tbody>
              {perUser.map((u) => (
                <tr key={u.userId}><td>{u.name || "—"}</td><td>{u.sent}</td><td>{u.templates}</td><td>{pct(u.read, u.sent)}</td><td>{u.failed}</td><td>{u.conversations}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="wa-block">
        <h4>Template performance</h4>
        {!templateUsage.length ? <div className="wa-help">No templates sent in this period.</div> : (
          <table className="wa-table">
            <thead><tr><th>Template</th><th>Category</th><th>Sent</th><th>Delivered</th><th>Read</th><th>Failed</th></tr></thead>
            <tbody>
              {templateUsage.map((t) => (
                <tr key={t.name}><td>{t.name}</td><td>{(t.category || "").toLowerCase()}</td><td>{t.sent}</td><td>{pct(t.delivered, t.sent)}</td><td>{pct(t.read, t.sent)}</td><td>{t.failed}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

// Numeric sending rules shown in the admin form (bounds come from the server).
const NUMERIC_RULES = [
  { key: "templateGapHours", label: "Wait between templates when there's no reply", unit: "hours", zero: "no wait" },
  { key: "maxUnansweredTemplates", label: "Max templates in a row without a reply", unit: "templates", zero: "no limit" },
  { key: "outboundPerUserPerHour", label: "Messages per team member per hour", unit: "per hour", zero: "no limit" },
];

const toRulesDraft = (r) => ({
  templateGapHours: String(r.templateGapHours),
  maxUnansweredTemplates: String(r.maxUnansweredTemplates),
  outboundPerUserPerHour: String(r.outboundPerUserPerHour),
  marketingRequiresEmailEngagement: r.marketingRequiresEmailEngagement,
});

/** Team sending rules (server: whatsapp/sendRules.js). */
const SendRulesCard = () => {
  const [rules, setRules] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setMessage(null);
    try {
      const res = await client.query({ query: WA_SEND_RULES, fetchPolicy: "network-only" });
      setRules(res.data.whatsappSendRules);
      setDraft(toRulesDraft(res.data.whatsappSendRules));
    } catch (err) {
      setMessage({ type: "danger", text: gqlErrorMessage(err) });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Per-field validation against the server's bounds.
  const errors = {};
  if (rules && draft) {
    NUMERIC_RULES.forEach(({ key }) => {
      const { min, max } = rules.fields[key];
      const raw = draft[key].trim();
      const n = Number(raw);
      if (raw === "" || !Number.isInteger(n) || n < min || n > max) errors[key] = `Whole number from ${min} to ${max}`;
    });
  }
  const valid = !Object.keys(errors).length;
  const parsed = draft && {
    ...Object.fromEntries(NUMERIC_RULES.map(({ key }) => [key, Number(draft[key])])),
    marketingRequiresEmailEngagement: draft.marketingRequiresEmailEngagement,
  };
  const dirty = !!(rules && parsed && Object.keys(parsed).some((k) => parsed[k] !== rules[k]));

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await client.mutate({ mutation: WA_SAVE_SEND_RULES, variables: parsed });
      const saved = { ...rules, ...res.data.whatsappSaveSendRules };
      setRules(saved);
      setDraft(toRulesDraft(saved));
      setMessage({ type: "ok", text: "Sending rules saved. They apply to the next message sent." });
    } catch (err) {
      setMessage({ type: "danger", text: gqlErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  const noLimits = valid && parsed && !parsed.templateGapHours && !parsed.maxUnansweredTemplates && !parsed.outboundPerUserPerHour;

  return (
    <div className="wa-block">
      <h4>Sending rules</h4>
      <div className="wa-help" style={{ marginBottom: 12 }}>
        Team policy on top of Meta's own rules (free text only within 24h of the contact's last message; marketing needs an opt-in), which can't be changed.
        A reply from the contact resets the template limits. Use 0 to turn a limit off.
      </div>
      {message && <div className={`wa-alert wa-alert--${message.type}`} style={{ marginBottom: 12 }}><div>{message.text}</div></div>}
      {!draft ? (
        !message && <div className="wa-help"><i className="fa-solid fa-spinner wa-spin" /> Loading…</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {NUMERIC_RULES.map(({ key, label, unit, zero }) => {
              const { max, default: def } = rules.fields[key];
              return (
                <div className="wa-field" key={key} style={{ flex: "1 1 220px", marginBottom: 0 }}>
                  <label className="wa-label" htmlFor={`wa-rule-${key}`}>{label}</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      id={`wa-rule-${key}`}
                      className="wa-input"
                      type="number"
                      inputMode="numeric"
                      min={rules.fields[key].min}
                      max={max}
                      step={1}
                      style={{ maxWidth: 120, borderColor: errors[key] ? "var(--wa-danger)" : undefined }}
                      value={draft[key]}
                      aria-invalid={!!errors[key]}
                      onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                    />
                    <span className="wa-help" style={{ margin: 0 }}>{!errors[key] && Number(draft[key]) === 0 ? zero : unit}</span>
                  </div>
                  <div className="wa-help" style={{ margin: "4px 0 0", color: errors[key] ? "var(--wa-danger)" : undefined }}>
                    {errors[key] || `0–${max} · default ${def}`}
                  </div>
                </div>
              );
            })}
          </div>
          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 16, cursor: "pointer" }}>
            <input
              type="checkbox"
              style={{ marginTop: 3 }}
              checked={draft.marketingRequiresEmailEngagement}
              onChange={(e) => setDraft((d) => ({ ...d, marketingRequiresEmailEngagement: e.target.checked }))}
            />
            <span>
              <b>Marketing templates only to leads who engaged with our email</b> (opened, clicked or replied)
              <div className="wa-help" style={{ margin: "2px 0 0" }}>
                Team policy, not a Meta rule. Meta only requires a recorded WhatsApp opt-in for marketing, and that is always enforced.
              </div>
            </span>
          </label>
          {noLimits && (
            <div className="wa-alert wa-alert--warn" style={{ marginTop: 12 }}>
              <i className="fa-solid fa-triangle-exclamation" />
              <div>All limits are off. Repeated unanswered or high-volume messages get the number blocked and reported, which lowers its quality rating and can restrict sending.</div>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
            <div className="wa-help" style={{ margin: 0, flex: 1 }}>
              {rules.updatedAt ? `Last changed ${fullTime(rules.updatedAt)} · history in Audit log.` : "Using defaults."}
            </div>
            {dirty && <button type="button" className="wa-btn" onClick={() => setDraft(toRulesDraft(rules))} disabled={saving}>Discard</button>}
            <button type="button" className="wa-btn wa-btn--primary" disabled={!dirty || !valid || saving} onClick={save}>
              {saving ? <i className="fa-solid fa-spinner wa-spin" /> : null} Save
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const TEMPLATE_STATUS = {
  APPROVED: { cls: "wa-chip--ok", label: "Approved" },
  PENDING: { cls: "wa-chip--warn", label: "In review" },
  REJECTED: { cls: "wa-chip--danger", label: "Rejected" },
  PAUSED: { cls: "wa-chip--warn", label: "Paused" },
  DISABLED: { cls: "wa-chip--danger", label: "Disabled" },
  IN_APPEAL: { cls: "wa-chip--warn", label: "In appeal" },
  PENDING_DELETION: { cls: "", label: "Deleting" },
  LIMIT_EXCEEDED: { cls: "wa-chip--danger", label: "Limit exceeded" },
};
// Plain-language help for Meta's rejection codes.
const REJECTION_HELP = {
  INVALID_FORMAT: "Formatting problem — check variables (numbered in order, not first/last), lengths and sample values.",
  PROMOTIONAL: "Meta read it as promotional — resubmit as a Marketing template or make it a plain update.",
  TAG_CONTENT_MISMATCH: "The category doesn't match the content — duplicate it with the other category.",
  INCORRECT_CATEGORY: "The category doesn't match the content — duplicate it with the other category.",
  ABUSIVE_CONTENT: "Meta flagged the wording. Rephrase it neutrally.",
  SCAM: "Meta flagged it as potentially misleading. Rephrase and avoid urgency or prize language.",
};
const TEMPLATE_FILTERS = [
  { key: "all", label: "All", match: () => true },
  { key: "approved", label: "Approved", match: (t) => t.status === "APPROVED" },
  { key: "review", label: "In review", match: (t) => ["PENDING", "IN_APPEAL"].includes(t.status) },
  { key: "attention", label: "Needs attention", match: (t) => ["REJECTED", "PAUSED", "DISABLED", "LIMIT_EXCEEDED"].includes(t.status) },
];
const PENDING_POLL_MS = 30 * 1000;
const TEMPLATES_UPDATED_EVENT = "wa:templates:updated";

const TemplatePreview = ({ t }) => (
  <div className="wa-preview" style={{ marginBottom: 10 }}>
    {t.headerText && <div style={{ fontWeight: 700, marginBottom: 4 }}>{t.headerText}</div>}
    <div className="wa-bubble__text">{t.bodyText}</div>
    {t.footerText && <div style={{ color: "var(--wa-muted)", fontSize: 12, marginTop: 6 }}>{t.footerText}</div>}
    {t.buttons?.length > 0 && (
      <div className="wa-tpl-row" style={{ marginTop: 8 }}>
        {t.buttons.map((b, i) => <span key={i} className="wa-chip wa-chip--accent">{b.text}</span>)}
      </div>
    )}
  </div>
);

/**
 * Templates tab: every template on the account (any status), create / edit /
 * duplicate / delete (editor panel is rendered by the page frame), and the
 * per-template auto-fill mapping.
 */
const TemplatesTab = ({ version, flash, onOpenEditor }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.query({ query: WA_TEMPLATES_ADMIN, fetchPolicy: "network-only" });
      setData(res.data.whatsappTemplatesAdmin);
    } catch (err) {
      setError(gqlErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, version]);
  useEffect(() => { if (flash) setMessage({ type: "ok", text: flash }); }, [flash, version]);

  // Meta's review result arrives by webhook → server emits; also poll while anything is in review.
  useEffect(() => {
    socket.on(TEMPLATES_UPDATED_EVENT, load);
    return () => socket.off(TEMPLATES_UPDATED_EVENT, load);
  }, [load]);
  const hasPending = !!data?.templates?.some((t) => t.status === "PENDING");
  useEffect(() => {
    if (!hasPending) return undefined;
    const timer = setInterval(load, PENDING_POLL_MS);
    return () => clearInterval(timer);
  }, [hasPending, load]);

  const keyOf = (t) => t.id || `${t.name}|${t.language}`;
  const varsFor = (t) => edits[keyOf(t)] || t.variables.map((v) => ({ source: v.source, label: v.label }));
  const setVar = (t, i, patch) => setEdits((prev) => ({ ...prev, [keyOf(t)]: varsFor(t).map((v, j) => (j === i ? { ...v, ...patch } : v)) }));

  const saveMap = async (t) => {
    setSaving(keyOf(t));
    setMessage(null);
    try {
      await client.mutate({ mutation: WA_SAVE_TEMPLATE_CONFIG, variables: { name: t.name, variables: varsFor(t) } });
      setMessage({ type: "ok", text: `Saved auto-fill for ${t.name}.` });
      setEdits((prev) => { const n = { ...prev }; delete n[keyOf(t)]; return n; });
      dispatch(fetchWaTemplates());
      load();
    } catch (err) {
      setMessage({ type: "danger", text: gqlErrorMessage(err) });
    } finally {
      setSaving(null);
    }
  };

  const remove = async (t) => {
    setDeleting(t.id);
    setMessage(null);
    try {
      const { data: res } = await client.mutate({ mutation: WA_DELETE_TEMPLATE, variables: { id: t.id } });
      const r = res.whatsappDeleteTemplate;
      if (!r?.ok) {
        setMessage({ type: "danger", text: r?.error || "Meta did not delete the template." });
        return;
      }
      setConfirmDelete(null);
      setMessage({ type: "ok", text: `Deleted ${t.name} (${t.language}). Meta blocks re-using this name for 30 days.` });
      dispatch(fetchWaTemplates());
      load();
    } catch (err) {
      setMessage({ type: "danger", text: gqlErrorMessage(err) });
    } finally {
      setDeleting(null);
    }
  };

  const meta = data ? { limits: data.limits, languages: data.languages, categories: data.categories } : null;
  const templates = data?.templates || [];
  const active = TEMPLATE_FILTERS.find((f) => f.key === filter) || TEMPLATE_FILTERS[0];
  const q = query.trim().toLowerCase();
  const shown = templates.filter((t) => active.match(t) && (!q || t.name.includes(q) || t.bodyText.toLowerCase().includes(q)));

  return (
    <>
      <SendRulesCard />

      <div className="wa-tpl-row" style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16, flex: 1 }}>Message templates</h3>
        <button type="button" className="wa-btn" onClick={() => { load(); dispatch(fetchWaTemplates({ refresh: true })); }} disabled={loading}>
          <i className={`fa-solid fa-rotate ${loading ? "wa-spin" : ""}`} /> Refresh from Meta
        </button>
        <button type="button" className="wa-btn wa-btn--primary" disabled={!meta} onClick={() => onOpenEditor({ mode: "create", template: null, meta })}>
          <i className="fa-solid fa-plus" /> New template
        </button>
      </div>

      <div className="wa-tpl-row" style={{ marginBottom: 12 }}>
        <nav className="wa-tabs" role="tablist" aria-label="Template status" style={{ padding: 0, border: "none", flex: "1 1 auto" }}>
          {TEMPLATE_FILTERS.map((f) => {
            const n = templates.filter(f.match).length;
            return (
              <button type="button" role="tab" key={f.key} aria-selected={filter === f.key} className={`wa-tab ${filter === f.key ? "wa-tab--active" : ""}`} onClick={() => setFilter(f.key)}>
                {f.label} <span className={`wa-tab__count ${f.key === "attention" && n ? "wa-tab__count--unread" : ""}`}>{n}</span>
              </button>
            );
          })}
        </nav>
        <input className="wa-input" style={{ maxWidth: 240 }} placeholder="Search templates" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search templates" />
      </div>

      {hasPending && (
        <div className="wa-alert wa-alert--info" style={{ marginBottom: 12 }}>
          <i className="fa-solid fa-hourglass-half" />
          <div>Templates in review refresh automatically. Meta usually decides within minutes, at most 24 hours — admins and the author get a notification.</div>
        </div>
      )}
      {message && <div className={`wa-alert wa-alert--${message.type}`} style={{ marginBottom: 12 }}><div>{message.text}</div></div>}
      {error && <div className="wa-alert wa-alert--danger" style={{ marginBottom: 12 }}><div style={{ flex: 1 }}>{error}</div><button type="button" className="wa-btn wa-btn--sm" onClick={load}>Retry</button></div>}
      {!data && !error && <div className="wa-help"><i className="fa-solid fa-spinner wa-spin" /> Loading templates from Meta…</div>}
      {data && !shown.length && (
        <div className="wa-block wa-help" style={{ textAlign: "center" }}>
          {templates.length ? "No templates match this filter." : "No templates yet — create your first one with New template."}
        </div>
      )}

      {shown.map((t) => {
        const st = TEMPLATE_STATUS[t.status] || { cls: "", label: String(t.status || "").toLowerCase() };
        const k = keyOf(t);
        return (
          <div className="wa-block" key={k}>
            <div className="wa-tpl-row" style={{ marginBottom: 8 }}>
              <h4 style={{ margin: 0, flex: "1 1 auto", wordBreak: "break-all" }}>{t.name}</h4>
              <span className={`wa-chip ${st.cls}`}>{st.label}</span>
              <span className={`wa-chip ${t.category === "MARKETING" ? "wa-chip--warn" : "wa-chip--accent"}`}>{(t.category || "").toLowerCase()}</span>
              <span className="wa-chip">{t.language}</span>
              {t.status === "APPROVED" && !t.sendable && <span className="wa-chip wa-chip--danger" title={t.unsupportedReason}>Not sendable from inbox</span>}
            </div>
            {t.createdByName && <div className="wa-help" style={{ marginTop: -4, marginBottom: 8 }}>Created here by {t.createdByName}</div>}
            {t.rejectedReason && (
              <div className="wa-alert wa-alert--danger" style={{ marginBottom: 10 }}>
                <i className="fa-solid fa-circle-exclamation" />
                <div><b>Rejected: {t.rejectedReason.replace(/_/g, " ").toLowerCase()}.</b> {REJECTION_HELP[t.rejectedReason] || "Edit the wording and resubmit."}</div>
              </div>
            )}
            <TemplatePreview t={t} />

            {t.variables.length > 0 && (
              <>
                <div className="wa-label">Auto-fill in the composer</div>
                <table className="wa-table">
                  <thead><tr><th>Variable</th><th>Auto-fill from</th><th>Label shown to the team</th></tr></thead>
                  <tbody>
                    {varsFor(t).map((v, i) => (
                      <tr key={i}>
                        <td><code>{`{{${t.variables[i].key}}}`}</code> <span className="wa-help">{t.variables[i].component}</span></td>
                        <td>
                          <select className="wa-select" value={v.source} onChange={(e) => setVar(t, i, { source: e.target.value })}>
                            {Object.entries(VARIABLE_SOURCE_LABELS).map(([s, label]) => <option key={s} value={s}>{label}</option>)}
                          </select>
                        </td>
                        <td><input className="wa-input" value={v.label} maxLength={120} onChange={(e) => setVar(t, i, { label: e.target.value })} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <div className="wa-tpl-row" style={{ justifyContent: "flex-end", marginTop: 10 }}>
              {confirmDelete === k ? (
                <>
                  <span className="wa-help" style={{ margin: 0, color: "var(--wa-danger)" }}>Delete {t.name} ({t.language}) from Meta? The name can't be reused for 30 days.</span>
                  <button type="button" className="wa-btn wa-btn--sm" onClick={() => setConfirmDelete(null)} disabled={deleting === t.id}>Cancel</button>
                  <button type="button" className="wa-btn wa-btn--sm wa-btn--danger" onClick={() => remove(t)} disabled={deleting === t.id}>
                    {deleting === t.id ? <i className="fa-solid fa-spinner wa-spin" /> : <i className="fa-solid fa-trash-can" />} Yes, delete
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="wa-btn wa-btn--sm wa-btn--ghost wa-btn--danger" onClick={() => setConfirmDelete(k)}><i className="fa-solid fa-trash-can" /> Delete</button>
                  <button type="button" className="wa-btn wa-btn--sm" onClick={() => onOpenEditor({ mode: "duplicate", template: t, meta })}><i className="fa-regular fa-copy" /> Duplicate</button>
                  {t.editable && (
                    <button type="button" className="wa-btn wa-btn--sm" onClick={() => onOpenEditor({ mode: "edit", template: t, meta })}>
                      <i className="fa-solid fa-pen" /> {t.status === "REJECTED" ? "Fix & resubmit" : "Edit"}
                    </button>
                  )}
                  {t.variables.length > 0 && (
                    <button type="button" className="wa-btn wa-btn--primary wa-btn--sm" disabled={!edits[k] || saving === k} onClick={() => saveMap(t)}>
                      {saving === k ? <i className="fa-solid fa-spinner wa-spin" /> : null} Save auto-fill
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};

const AuditTab = () => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  useEffect(() => {
    client.query({ query: WA_AUDIT_LOG, variables: { limit: 300 }, fetchPolicy: "network-only" })
      .then(({ data }) => setRows(data.whatsappAuditLog || []))
      .catch((err) => setError(gqlErrorMessage(err)));
  }, []);
  if (error) return <div className="wa-alert wa-alert--danger">{error}</div>;
  if (!rows) return <div className="wa-help"><i className="fa-solid fa-spinner wa-spin" /> Loading…</div>;
  const actions = [...new Set(rows.map((r) => r.action))].sort();
  const shown = filter ? rows.filter((r) => r.action === filter) : rows;
  return (
    <>
      <div style={{ marginBottom: 12, maxWidth: 260 }}>
        <select className="wa-select" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter by action">
          <option value="">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{a.replace(/_/g, " ")}</option>)}
        </select>
      </div>
      <table className="wa-table">
        <thead><tr><th>When</th><th>Who</th><th>Action</th><th>Details</th></tr></thead>
        <tbody>
          {shown.map((r) => (
            <tr key={r.id}><td style={{ whiteSpace: "nowrap" }}>{fullTime(r.createdAt)}</td><td>{r.actorName || "—"}</td><td>{r.action.replace(/_/g, " ")}</td><td>{r.summary}</td></tr>
          ))}
          {!shown.length && <tr><td colSpan={4} className="wa-help">No entries.</td></tr>}
        </tbody>
      </table>
    </>
  );
};

const WhatsAppAdmin = () => {
  const dispatch = useDispatch();
  const { status, statusLoaded } = useSelector((s) => s.whatsapp);
  const [tab, setTab] = useState("overview");
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [editor, setEditor] = useState(null); // { mode, template, meta } — rendered in the fixed frame
  const [tplVersion, setTplVersion] = useState(0);
  const [tplFlash, setTplFlash] = useState(null);

  useEffect(() => { dispatch(fetchWaStatus()); }, [dispatch]);
  useEffect(() => { if (status?.canManage) dispatch(fetchWaQuickReplies()); }, [dispatch, status?.canManage]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await client.query({ query: WA_ADMIN_OVERVIEW, variables: { days }, fetchPolicy: "network-only" });
      setData(res.data.whatsappAdminOverview);
    } catch (err) {
      setError(gqlErrorMessage(err));
    }
  }, [days]);

  useEffect(() => { if (status?.canManage) load(); }, [status?.canManage, load]);

  const refreshHealth = async () => {
    setRefreshing(true);
    try {
      await client.mutate({ mutation: WA_REFRESH_HEALTH });
      await load();
    } catch (err) {
      setError(gqlErrorMessage(err));
    } finally {
      setRefreshing(false);
    }
  };

  if (!statusLoaded) return <div className="wa-root wa-admin"><div className="wa-help"><i className="fa-solid fa-spinner wa-spin" /> Loading…</div></div>;
  if (!status?.canManage) {
    return (
      <div className="wa-root wa-admin"><div className="wa-gate" style={{ margin: "40px auto" }}>
        <i className="fa-solid fa-lock wa-empty__icon" style={{ fontSize: 40 }} />
        <h3>WhatsApp admin only</h3>
        <p>Ask an administrator to grant <b>WhatsApp Admin</b> in Access Management.</p>
      </div></div>
    );
  }

  const onTemplateSaved = (saved, mode) => {
    setEditor(null);
    setTplFlash(mode === "edit"
      ? `Changes to ${saved.name} sent to Meta for review.`
      : `${saved.name} submitted to Meta for review${saved.category ? ` as ${String(saved.category).toLowerCase()}` : ""}. You'll be notified when it's decided.`);
    setTplVersion((v) => v + 1);
  };

  return (
    <div className="wa-root wa-admin-frame">
    <div className="wa-admin">
      <div className="wa-admin__head">
        <h2><i className="fa-brands fa-whatsapp" style={{ color: "var(--wa-accent)" }} /> WhatsApp Admin</h2>
        {tab === "overview" && <button type="button" className="wa-btn" onClick={load}><i className="fa-solid fa-rotate" /> Refresh</button>}
      </div>
      <nav className="wa-admin__tabs wa-tabs" role="tablist" style={{ padding: 0 }}>
        {TABS.map((t) => (
          <button type="button" role="tab" key={t.key} aria-selected={tab === t.key} className={`wa-tab ${tab === t.key ? "wa-tab--active" : ""}`} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </nav>
      {error && <div className="wa-alert wa-alert--danger" style={{ marginBottom: 12 }}>{error}</div>}
      {tab === "overview" && (data ? <Overview data={data} days={days} setDays={setDays} onRefreshHealth={refreshHealth} refreshing={refreshing} /> : !error && <div className="wa-help"><i className="fa-solid fa-spinner wa-spin" /> Loading…</div>)}
      {tab === "templates" && <TemplatesTab version={tplVersion} flash={tplFlash} onOpenEditor={setEditor} />}
      {tab === "quick" && (
        <div style={{ position: "relative", minHeight: 560 }}>
          <QuickRepliesPanel onClose={() => setTab("overview")} />
        </div>
      )}
      {tab === "audit" && <AuditTab />}
    </div>
    {editor && (
      <TemplateEditorPanel
        key={`${editor.mode}-${editor.template?.id || "new"}`}
        mode={editor.mode}
        template={editor.template}
        meta={editor.meta}
        onClose={() => setEditor(null)}
        onSaved={onTemplateSaved}
      />
    )}
    </div>
  );
};

export default WhatsAppAdmin;
